import { IconButton, Icons, Stack, Tooltip, Typography } from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  getDocumentFile,
  getGetPatientDocumentsQueryKey,
  useDeleteDocument,
  useGetPatientDocumentsSuspense,
  type PatientDocumentDto,
} from '../../../api/created/documents.ts';
import { DataTable, type DataTableColumn } from '../../../components/table/DataTable.tsx';
import { formatDateWithGermanMonth } from '../../../utils/date.ts';
import { getDocumentCategoryName } from '../../../utils/document.ts';
import { base64ToBlob, downloadBlob, getMimeTypeFromFileExtension } from '../../../utils/file.ts';
import { DeleteConfirmationDialog } from '../../components/DeleteConfirmationDialog.tsx';

export const Route = createFileRoute('/patients/$patientId/document')({
  component: PatientDocumentRoute,
  staticData: { title: 'Dokumente' },
});

function DocumentRowActions({
  document,
  isDownloading,
  onDownload,
  onDelete,
}: {
  document: PatientDocumentDto;
  isDownloading: boolean;
  onDownload: (document: PatientDocumentDto) => void;
  onDelete: (document: PatientDocumentDto) => void;
}) {
  return (
    <Stack direction="row" gap={0.5} onClick={(e) => e.stopPropagation()}>
      <Tooltip title="Herunterladen">
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          loading={isDownloading}
          onClick={() => onDownload(document)}>
          <Icons.Download />
        </IconButton>
      </Tooltip>
      <Tooltip title="Entfernen">
        <IconButton size="sm" variant="plain" color="neutral" onClick={() => onDelete(document)}>
          <Icons.Delete />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function PatientDocumentRoute() {
  const { patientId } = Route.useParams();
  const queryClient = useQueryClient();
  const { data: documents } = useGetPatientDocumentsSuspense(patientId);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<PatientDocumentDto | null>(null);

  const deleteDocumentMutation = useDeleteDocument({
    onSuccess: () => {
      toast.success('Dokument wurde erfolgreich gelöscht');
      setDocumentToDelete(null);
      void queryClient.invalidateQueries({ queryKey: getGetPatientDocumentsQueryKey(patientId) });
    },
    onError: () => {
      toast.error('Beim Löschen des Dokuments ist ein Fehler aufgetreten');
    },
  });

  const handleDownload = async (document: PatientDocumentDto) => {
    setDownloadingFileId(document.fileId);
    try {
      const data = await getDocumentFile(document.fileId);
      const blob = base64ToBlob(data.file, getMimeTypeFromFileExtension(data.fileName ?? document.fileName));
      downloadBlob(blob, data.fileName ?? document.fileName);
    } catch {
      toast.error('Beim Herunterladen des Dokuments ist ein Fehler aufgetreten');
    } finally {
      setDownloadingFileId(null);
    }
  };

  const columns: DataTableColumn<PatientDocumentDto>[] = [
    {
      key: 'category',
      header: 'Kategorie',
      width: 280,
      render: (document) => <Typography>{getDocumentCategoryName(document.category)}</Typography>,
    },
    {
      key: 'fileName',
      header: 'Dateiname',
      render: (document) => <Typography>{document.fileName}</Typography>,
    },
    {
      key: 'lastUpdated',
      header: 'Zuletzt aktualisiert',
      width: 180,
      render: (document) => <Typography>{formatDateWithGermanMonth(document.lastUpdated)}</Typography>,
    },
    {
      key: 'actions',
      header: 'Aktion',
      width: 120,
      disableTooltip: true,
      render: (document) => (
        <DocumentRowActions
          document={document}
          isDownloading={downloadingFileId === document.fileId}
          onDownload={handleDownload}
          onDelete={setDocumentToDelete}
        />
      ),
    },
  ];

  return (
    <Stack flex={1} gap={2}>
      <Typography level="h3">Dokumente</Typography>
      <DataTable
        columns={columns}
        rows={documents}
        getRowKey={(document) => document.fileId}
        emptyText="Keine Dokumente vorhanden."
      />
      <DeleteConfirmationDialog
        open={documentToDelete !== null}
        onCancel={() => setDocumentToDelete(null)}
        onDelete={() => {
          if (documentToDelete) {
            deleteDocumentMutation.mutate(documentToDelete.fileId);
          }
        }}
        modalTitle="Dokument löschen">
        <Typography>Ausgewähltes Dokument wirklich löschen?</Typography>
      </DeleteConfirmationDialog>
    </Stack>
  );
}
